import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {OutputMode, YearOrdering} from './date-picker.enum';
import {Observable} from 'rxjs';
import {isNumber, isString} from 'util';
import * as _ from 'lodash';
import * as moment from 'moment';
import {Subject} from 'rxjs';


@Injectable()
export class DatePickerService {
  // Constants
  now: Observable<moment.Moment> = Observable.of(moment());
  monthNames = moment.monthsShort();
  _100yearsAgo = this.now.map(d => d.clone().subtract(100, 'years'));
  _100yearsFromNow = this.now.map(d => d.clone().add(100, 'years'));
  _0_to_11_months = _.range(0, 12).map(i => <[number, string]>[i, this.monthNames[i]]);
  _1_to_31 = _.range(1, 32);

  // Value
  input: Subject<string | null | Date | moment.Moment> = new Subject();  // 0 index
  month: BehaviorSubject<number> = new BehaviorSubject(null);  // 0 index
  day: BehaviorSubject<number> = new BehaviorSubject(null);
  year: BehaviorSubject<number> = new BehaviorSubject(null);

  // Input
  min: BehaviorSubject<string> = new BehaviorSubject(null);
  max: BehaviorSubject<string> = new BehaviorSubject(null);
  yearOrdering: BehaviorSubject<YearOrdering> = new BehaviorSubject(YearOrdering.descending);
  outputMode: BehaviorSubject<OutputMode> = new BehaviorSubject(OutputMode.full);

  // Output
  outputMoment: Observable<moment.Moment> = Observable
    .combineLatest(this.year, this.month, this.day)
    .map(([y, m, d]) => {
      if (isNumber(y) && isNumber(m)) {
        if (isNumber(d)) {
          return moment([y, m, d]);
        } else {
          return moment([y, m]);
        }
      }
      return null;
    });
  yearMonth: Observable<string> = this.outputMoment
    .map(m => m && m.format('YYYY-MM'));
  iso8601: Observable<string> = this.outputMoment.map(m => m && m.format('YYYY-MM-DD'));
  outputDate: Observable<Date> = this.outputMoment.map(m => m && m.toDate());
  output: Observable<string | null | Date | moment.Moment> = Observable
    .merge(this.year, this.month, this.day, this.outputMode)
    .flatMap(() => this.outputMode)
    .flatMap((outputMode: OutputMode) => {
      switch (outputMode) {
        case OutputMode.full:
          return this.iso8601;
        case OutputMode.yearMonth:
          return this.yearMonth;
        case OutputMode.date:
          return this.outputDate;
        case OutputMode.moment:
          return this.outputMoment;
        default:
          return Observable.of(null);
      }
    }).map(o => o || null);

  // Derivatives
  minMoment: Observable<moment.Moment> = this.min.map(min => min && moment(min));
  maxMoment: Observable<moment.Moment> = this.max.map(max => max && moment(max));
  minMaxHasSense: Observable<boolean> = Observable.combineLatest(this.minMoment, this.maxMoment)
    .map(([min, max]) => {
      if (min && max) {
        return !min.isAfter(max);
      }
      return true;
    });
  minYearDropDown: Observable<number> = Observable
    .combineLatest(this.minMoment, this.maxMoment)
    .flatMap(([minMoment, maxMoment]) => {
      if (minMoment) {
        return Observable.of(minMoment);
      }
      if (maxMoment) {
        return Observable.of(maxMoment.clone().subtract(100, 'years'));
      }
      return this._100yearsAgo;
    })
    .map(d => d && d.get('year'));
  maxYearDropDown: Observable<number> = Observable
    .combineLatest(this.minMoment, this.maxMoment)
    .flatMap(([minMoment, maxMoment]) => {
      if (maxMoment) {
        return Observable.of(maxMoment);
      }
      if (minMoment) {
        return Observable.of(minMoment.clone().add(100, 'years'));
      }
      return this._100yearsFromNow;
    })
    .map(d => d && d.get('year'));
  minYearMonth: Observable<string> = this.minMoment.map(d => d && d.format('YYYY-MM'));
  maxYearMonth: Observable<string> = this.maxMoment.map(d => d && d.format('YYYY-MM'));
  minDay: Observable<number> = this.minMoment.map(d => d && d.get('date'));
  maxDay: Observable<number> = this.maxMoment.map(d => d && d.get('date'));
  hasAnyDatePart: Observable<boolean> = Observable
    .combineLatest(this.year, this.month, this.day)
    .map(([y, m, d]) => isNumber(y) && isNumber(m) && isNumber(d));

  // Choices
  yearList: Observable<number[]> = Observable.combineLatest(
    this.minMaxHasSense, this.minYearDropDown, this.maxYearDropDown, this.yearOrdering,
  ).map(([minMaxHasSense, minYear, maxYear, yearOrdering]) => {

    // If min and max don't make sense, don't bother
    if (!minMaxHasSense) {
      return [];
    }

    let yearList = _.range(minYear, maxYear + 1);
    if (yearOrdering === YearOrdering.descending) {
      yearList = yearList.reverse();
    }
    return yearList;
  });
  monthList: Observable<[number, string][]> = Observable.combineLatest(
    this.minMaxHasSense, this.minMoment, this.maxMoment, this.year,
  ).map(([hasSense, minMoment, maxMoment, choosenYear]) => {

    // If min and max don't make sense, don't bother
    if (!hasSense) {
      return [];
    }

    // If no min or max, don't bother
    if (!minMoment && !maxMoment) {
      return this._0_to_11_months;
    }

    const hasChoosenYear = isNumber(choosenYear);
    const minYear = minMoment && minMoment.get('year');
    const maxYear = maxMoment && maxMoment.get('year');
    const minMonth = minMoment && minMoment.get('month');
    const maxMonth = maxMoment && maxMoment.get('month');
    const minMaxSameYear = minYear === maxYear;
    let finalMinMonth = 0;
    let finalMaxMonth = 11;

    // If min and max doesn't make sense, return empty
    if (minMoment && maxMoment && minMoment.isAfter(maxMoment)) {
      return [];
    }

    // Bound months
    if (hasChoosenYear && minYear === choosenYear || minMaxSameYear) {
      finalMinMonth = minMonth;
    }
    if (hasChoosenYear && maxYear === choosenYear || minMaxSameYear) {
      finalMaxMonth = maxMonth;
    }

    return _.range(finalMinMonth, finalMaxMonth + 1).map(i => <[number, string]>[i, this.monthNames[i]]);
  });
  dayList: Observable<number[]> = Observable.combineLatest(
    this.minMaxHasSense,
    this.minYearMonth, this.maxYearMonth,
    this.minDay, this.maxDay,
    this.yearMonth,
  ).map(([hasSense, minYearMonth, maxYearMonth, minDay, maxDay, choosenYearMonth]) => {

    // If min and max don't make sense, don't bother
    if (!hasSense) {
      return [];
    }

    // If min and max are the same month year
    if (minYearMonth && maxYearMonth && minYearMonth === maxYearMonth) {
      return _.range(minDay, maxDay + 1);
    }

    // If month and year hasn't been chosen return 1 to 31
    if (!choosenYearMonth) {
      return this._1_to_31;
    }

    let finalMinDay = 1;
    let finalMaxDay = 31;

    if (choosenYearMonth) {
      if (choosenYearMonth === minYearMonth) {
        finalMinDay = minDay;
      }
      if (choosenYearMonth === maxYearMonth) {
        finalMaxDay = maxDay;
      } else if (choosenYearMonth) {
        finalMaxDay = moment(choosenYearMonth, 'YYYY-MM').endOf('month').get('date');
      }
    }

    return _.range(finalMinDay, finalMaxDay + 1);
  });

  monthListValues: Observable<number[]> = this.monthList.map(mL => mL.map(([i]) => i));

  constructor() {
    this.input.subscribe(input => this.setInboundValue(input));
    Observable.combineLatest(
      this.yearList,
      this.year,
      this.monthListValues,
      this.month,
      this.dayList,
      this.day,
      this.outputMoment,
    ).debounceTime(5).subscribe((values) => {
      const [yearList, year, monthListValues, month, dayList, day, momentValue] =
        <[number[], number, number[], number, number[], number, moment.Moment]>values;

      if (isNumber(year) && !yearList.includes(year)) {
        this.year.next(null);
      }
      if (isNumber(month) && !monthListValues.includes(month)) {
        this.month.next(null);
      }
      if (isNumber(day)) {
        if (!dayList.includes(day)) {
          this.day.next(null);
        } else if (momentValue && day > momentValue.get('date')) {
          this.day.next(null);
        }
      }
    });
  }

  private setInboundValue(input) {
    if (!input) {
      this.year.next(null);
      this.month.next(null);
      this.day.next(null);
    } else {
      if (isString(input)) {
        input = moment(input, 'YYYY-MM-DD');
      }
      if (moment.isMoment(input)) {
        const date: moment.Moment = <moment.Moment>input;
        this.year.next(date.get('year'));
        this.month.next(date.get('month'));
        this.day.next(date.get('date'));
      } else if (moment.isDate(input)) {
        const date: Date = <Date>input;
        this.year.next(date.getFullYear());
        this.month.next(date.getMonth());
        this.day.next(date.getDate());
      }
    }
  }
}
